// React Query + realtime hooks for the Live Coach surface.
//
// The persisted trail (`GET /coach/events`, a flat array of CoachEvent rows) seeds both panels; the
// ask mutation (`POST /coach/ask`) returns the answer event. The nudge stream is realtime:
// `useLiveCoachStream` owns a WebSocket subscription, reduces the live `{kind}` frames into a view
// model, and MERGES them over the seeded history so a panel opened mid-round shows past nudges too.
// Chat frames arriving over the WS (`coach.question`/`coach.answer`) trigger a trail refetch so the
// RIGHT copilot stays current without polling.

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInterviewIntelligenceWS } from '@/hooks/useInterviewIntelligenceWS';
import {
  askCoach,
  CATEGORY_COVERAGE,
  getSession,
  isNudgeCategory,
  listCoachEvents,
  type CoachCoverageEvent,
  type CoachEvent,
  type CoachNudgeEvent,
  type CoachSession,
  type CoachWSEvent,
  type NudgeCategory,
} from './api';

type SessionId = number | string | undefined;

const eventsKey = (sessionId: SessionId) => ['live-coach-events', sessionId];

export function useCoachEvents(sessionId: SessionId, enabled = true) {
  return useQuery<CoachEvent[]>({
    queryKey: eventsKey(sessionId),
    queryFn: () => listCoachEvents(sessionId!),
    enabled: enabled && Boolean(sessionId),
  });
}

export function useSession(sessionId: SessionId, enabled = true) {
  return useQuery<CoachSession | null>({
    queryKey: ['live-coach-session', sessionId],
    queryFn: () => getSession(sessionId!),
    enabled: enabled && Boolean(sessionId),
    // Poll until the meeting has started (started_at lands); it never changes after, so stop then.
    refetchInterval: (query) => (query.state.data?.started_at ? false : 3000),
  });
}

/**
 * Per-second elapsed time since the meeting started, anchored to the server's `started_at` (a stable,
 * React-Query-cached timestamp). Because it's purely `now − started_at`, it survives prep/coach tab
 * remounts (the cached session re-seeds instantly) and stays accurate. Freezes at `ended_at` once the
 * session ends, and reads 0 until it's live.
 */
export function useMeetingElapsed(
  session: CoachSession | null | undefined
): number {
  const startedMs = session?.started_at ? Date.parse(session.started_at) : null;
  const endedMs = session?.ended_at ? Date.parse(session.ended_at) : null;
  // `now` is seeded once (lazy init) and advanced by the interval — never read off the clock in render.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (startedMs === null || endedMs !== null) return; // not started, or ended (frozen) — don't tick
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedMs, endedMs]);
  if (startedMs === null) return 0;
  return Math.max(0, (endedMs ?? nowMs) - startedMs);
}

export function useAskCoach(sessionId: SessionId) {
  const qc = useQueryClient();
  return useMutation<CoachEvent, unknown, { question: string; atMs: number }>({
    mutationFn: ({ question, atMs }) => askCoach(sessionId!, question, atMs),
    onSuccess: () => {
      // The POST returns only the answer; refetch the trail so both the question and answer rows
      // (threaded by parent_event_id) land together.
      qc.invalidateQueries({ queryKey: eventsKey(sessionId) });
    },
  });
}

/* ----------------------------- realtime nudge stream ----------------------------- */

// A nudge as held in the LEFT-panel stream, plus the `addressed` flag (auto-resolution).
export interface StreamNudge {
  id: number;
  category: NudgeCategory;
  // The observation — what the coach noticed.
  title: string;
  // The ready-to-say ask.
  text: string;
  // The grounding — why this nudge fired.
  why: string;
  at_ms: number;
  addressed: boolean;
}

export interface ScreenContext {
  screen_state: string;
  what_changed: string;
}

// A live WS nudge frame → the stream's view model.
const nudgeFromWS = (e: CoachNudgeEvent): StreamNudge => ({
  id: e.id,
  category: e.category,
  title: e.title,
  text: e.text,
  why: e.why,
  at_ms: e.at_ms,
  addressed: false,
});

// A live coverage frame → a stream card (the auto-nudge: "✓ X covered. Still to assess: Y, Z"). It also
// updates the checklist (coveredAreas) — see the reducer.
const nudgeFromCoverage = (e: CoachCoverageEvent): StreamNudge => ({
  id: e.id,
  category: CATEGORY_COVERAGE,
  title: e.area,
  text: e.text,
  why: '',
  at_ms: e.at_ms,
  addressed: false,
});

// A persisted CoachEvent nudge row (category 0–5) → the stream's view model. citation → why,
// acknowledged_at → addressed.
const nudgeFromEvent = (e: CoachEvent): StreamNudge | null => {
  if (!isNudgeCategory(e.category)) return null;
  return {
    id: e.id,
    category: e.category,
    title: e.title ?? '',
    text: e.text,
    why: e.citation ?? '',
    at_ms: e.at_ms ?? 0,
    addressed: e.acknowledged_at !== null,
  };
};

// Live state owned by the WS reducer: nudges/resolutions seen on this connection, the latest screen,
// and the running clock. Seeded history is merged in by the hook, not stored here.
interface StreamState {
  liveNudges: StreamNudge[];
  // ids the WS resolved this session — applied over both live and seeded nudges.
  resolvedIds: Set<number>;
  // Focus areas marked covered this session (names) — merged with seeded coverage to drive the checklist.
  coveredAreas: Set<string>;
  screen: ScreenContext | null;
  // High-water mark of every `at_ms` seen — drives the transport's elapsed clock without a wall timer.
  clockMs: number;
  // Transient progress line while the copilot runs a tool ("Pulling the scorecard…"); cleared when the
  // question is asked or its answer lands.
  toolUse: string | null;
  // The meeting feed's health, from the backend's stream.live/stream.lost events. 'unknown' until the first
  // one arrives. Distinct from the panel's OWN ws health (that's tracked by useInterviewIntelligenceWS).
  streamStatus: 'live' | 'lost' | 'unknown';
}

type StreamAction = { type: 'event'; event: CoachWSEvent } | { type: 'reset' };

const initialStreamState: StreamState = {
  liveNudges: [],
  resolvedIds: new Set(),
  coveredAreas: new Set(),
  screen: null,
  clockMs: 0,
  toolUse: null,
  streamStatus: 'unknown',
};

function streamReducer(state: StreamState, action: StreamAction): StreamState {
  if (action.type === 'reset')
    return {
      ...initialStreamState,
      resolvedIds: new Set(),
      coveredAreas: new Set(),
    };
  const e = action.event;
  const bumpClock = (at?: number) =>
    typeof at === 'number' ? Math.max(state.clockMs, at) : state.clockMs;

  switch (e.kind) {
    case 'transcript':
      return { ...state, clockMs: bumpClock(e.at_ms) };
    case 'screen.state':
      return {
        ...state,
        screen: { screen_state: e.screen_state, what_changed: e.what_changed },
        clockMs: bumpClock(e.at_ms),
      };
    case 'coach.nudge': {
      // Idempotent on id — a reconnect can replay a nudge we already hold.
      if (state.liveNudges.some((n) => n.id === e.id)) {
        return { ...state, clockMs: bumpClock(e.at_ms) };
      }
      return {
        ...state,
        liveNudges: [...state.liveNudges, nudgeFromWS(e)],
        clockMs: bumpClock(e.at_ms),
      };
    }
    case 'coach.resolved': {
      const resolvedIds = new Set(state.resolvedIds);
      resolvedIds.add(e.id);
      return { ...state, resolvedIds };
    }
    case 'coach.coverage': {
      // Coverage drives the rail checklist AND surfaces as a stream card (the auto-nudge). Idempotent on id.
      const coveredAreas = new Set(state.coveredAreas);
      e.covered.forEach((a) => coveredAreas.add(a));
      const exists = state.liveNudges.some((n) => n.id === e.id);
      return {
        ...state,
        coveredAreas,
        liveNudges: exists
          ? state.liveNudges
          : [...state.liveNudges, nudgeFromCoverage(e)],
        clockMs: bumpClock(e.at_ms),
      };
    }
    case 'coach.tool_use':
      // The copilot is pulling a tool for the pending question — surface its progress line.
      return { ...state, toolUse: e.label };
    case 'coach.question':
    case 'coach.answer':
      // Chat turns are reconciled into the React Query trail by the consumer, not the stream view. A new
      // question (or the arriving answer) ends any tool-progress line.
      return { ...state, toolUse: null, clockMs: bumpClock(e.at_ms) };
    case 'stream.live':
      return { ...state, streamStatus: 'live' };
    case 'stream.lost':
      return { ...state, streamStatus: 'lost' };
    default:
      return state;
  }
}

// Recall marks a broken realtime endpoint failed after ~90s of retries; give it a little slack.
const FEED_GIVE_UP_MS = 110_000;

// The single connection state the badge renders — the WORSE of two independent signals: the panel's own ws
// to the server ('service') and the meeting feed Recall streams to the server ('feed'). Both must be healthy
// to read 'live'. A terminal state means a refresh is needed ('service') or won't even help ('feed').
export type LiveConnection =
  | { state: 'live' }
  | { state: 'reconnecting'; scope: 'service' | 'feed' }
  | { state: 'disconnected'; scope: 'service' | 'feed' };

function deriveConnection(args: {
  connected: boolean;
  reconnecting: boolean;
  failed: boolean;
  streamStatus: 'live' | 'lost' | 'unknown';
  feedFailed: boolean;
}): LiveConnection {
  // Worst wins. The panel↔server link first (if it's down, nothing flows), then the meeting feed.
  if (args.failed) return { state: 'disconnected', scope: 'service' };
  if (args.reconnecting || !args.connected)
    return { state: 'reconnecting', scope: 'service' };
  if (args.feedFailed) return { state: 'disconnected', scope: 'feed' };
  if (args.streamStatus === 'lost')
    return { state: 'reconnecting', scope: 'feed' };
  return { state: 'live' };
}

export interface UseLiveCoachStreamResult {
  nudges: StreamNudge[];
  // Focus-area names the coach has marked covered so far — drives the rail checklist.
  coveredAreas: string[];
  screen: ScreenContext | null;
  clockMs: number;
  connected: boolean;
  reconnecting: boolean;
  // The combined connection state for the header badge (worse of panel↔server and the meeting feed).
  connection: LiveConnection;
  // Progress line while the copilot runs a tool ("Pulling the scorecard…"), else null.
  toolUse: string | null;
}

/**
 * Owns the realtime nudge stream for the LEFT panel. Seeds past nudges from the persisted trail (so a
 * panel opened mid/post-round shows history), then merges live WS frames idempotently by id — live
 * frames win on conflict. Resolutions (`coach.resolved`) and persisted `acknowledged_at` both mark a
 * nudge addressed. Mount this exactly once per open session.
 */
export function useLiveCoachStream(
  sessionId: SessionId,
  enabled = true
): UseLiveCoachStreamResult {
  const qc = useQueryClient();
  const [state, dispatch] = useReducer(streamReducer, initialStreamState);
  const { connected, reconnecting, failed, reconnectGeneration, subscribe } =
    useInterviewIntelligenceWS(sessionId, enabled);

  // The meeting feed can be dead for good: Recall retries a broken realtime endpoint for ~90s and then marks
  // it failed permanently — no more frames, and a page refresh won't recover it. So if the feed stays 'lost'
  // past that window (our own ws to the server is fine the whole time), surface a TERMINAL feed error.
  const [feedFailed, setFeedFailed] = useState(false);
  const feedLostSinceRef = useRef(0);
  useEffect(() => {
    feedLostSinceRef.current =
      state.streamStatus === 'lost'
        ? feedLostSinceRef.current || Date.now()
        : 0;
  }, [state.streamStatus]);
  useEffect(() => {
    const iv = setInterval(() => {
      const since = feedLostSinceRef.current;
      setFeedFailed(since > 0 && Date.now() - since > FEED_GIVE_UP_MS);
    }, 5_000);
    return () => clearInterval(iv);
  }, []);

  // Seed past nudges from the same trail the copilot reads (shared cache key — one fetch feeds both).
  const { data: events = [] } = useCoachEvents(sessionId, enabled);
  const seededNudges = useMemo(
    () =>
      events.map(nudgeFromEvent).filter((n): n is StreamNudge => n !== null),
    [events]
  );

  // Seed the covered set from the trail's coverage-PROGRESS rows so a panel opened mid/post-round shows the
  // checklist state. Each such row's title is the area(s) it marked covered. Category 1 is SHARED with
  // must-cover-at-risk *nudges* ("X still uncovered"), which must NOT seed the checklist — the leading "✓"
  // covered marker (Go coverageText) is the contract that tells a progress row from a gap nudge on the trail.
  const seededCovered = useMemo(() => {
    const set = new Set<string>();
    events
      .filter(
        (e) =>
          e.category === CATEGORY_COVERAGE && e.title && e.text.startsWith('✓')
      )
      .forEach((e) =>
        e
          .title!.split(',')
          .map((a) => a.trim())
          .filter(Boolean)
          .forEach((a) => set.add(a))
      );
    return set;
  }, [events]);

  useEffect(() => {
    if (!enabled || !sessionId) return;
    const unsubscribe = subscribe((event) => {
      dispatch({ type: 'event', event });
      // Every PERSISTED coach frame must refresh the trail cache, or a pane that remounts (e.g. tab away
      // to Prep and back) re-seeds from a stale trail and loses live nudges + coverage ticks. coverage_status
      // is transient (not persisted), so it's excluded.
      if (
        event.kind === 'coach.question' ||
        event.kind === 'coach.answer' ||
        event.kind === 'coach.nudge' ||
        event.kind === 'coach.resolved' ||
        event.kind === 'coach.coverage'
      ) {
        qc.invalidateQueries({ queryKey: eventsKey(sessionId) });
      }
    });
    return unsubscribe;
  }, [enabled, sessionId, subscribe, qc]);

  // After a reconnect, backfill the trail over HTTP — we may have dropped frames in the gap.
  useEffect(() => {
    if (reconnectGeneration > 0 && sessionId) {
      qc.invalidateQueries({ queryKey: eventsKey(sessionId) });
    }
  }, [reconnectGeneration, sessionId, qc]);

  // A new session starts a fresh live stream (the seed re-fetches via its own query key).
  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [sessionId]);

  // Merge: seeded history first, then live frames win on id. Apply WS resolutions over both. Sorted
  // by at_ms so a late-arriving seed slots into chronological order.
  const nudges = useMemo(() => {
    const byId = new Map<number, StreamNudge>();
    seededNudges.forEach((n) => byId.set(n.id, n));
    state.liveNudges.forEach((n) => byId.set(n.id, n));
    return Array.from(byId.values())
      .map((n) => (state.resolvedIds.has(n.id) ? { ...n, addressed: true } : n))
      .sort((a, b) => a.at_ms - b.at_ms);
  }, [seededNudges, state.liveNudges, state.resolvedIds]);

  // Union of seeded (trail) + live coverage, case preserved. The checklist matches names case-insensitively.
  const coveredAreas = useMemo(() => {
    const set = new Set<string>(seededCovered);
    state.coveredAreas.forEach((a) => set.add(a));
    return Array.from(set);
  }, [seededCovered, state.coveredAreas]);

  const connection = useMemo(
    () =>
      deriveConnection({
        connected,
        reconnecting,
        failed,
        streamStatus: state.streamStatus,
        feedFailed,
      }),
    [connected, reconnecting, failed, state.streamStatus, feedFailed]
  );

  return useMemo(
    () => ({
      nudges,
      coveredAreas,
      screen: state.screen,
      clockMs: state.clockMs,
      connected,
      reconnecting,
      connection,
      toolUse: state.toolUse,
    }),
    [
      nudges,
      coveredAreas,
      state.screen,
      state.clockMs,
      connected,
      reconnecting,
      connection,
      state.toolUse,
    ]
  );
}
